import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles'
import CallMadeIcon from '@mui/icons-material/CallMade';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import { Button, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import ColorConstants from '../../../defaults/ColorConstants';
import FieldNames from '../../../defaults/FieldNames.json';
import { useIFPRSGlobalContext } from '../../../IFPRSGlobalContext';
import { setActiveFilter } from '../../../redux/slices/customFilterSlice';
import { setDefinitionExpression, setFilterExpression, setCustomFilterCount } from '../../../redux/slices/filterSlice';
import CustomFilterService from '../../../services/CustomFilterService';
import ErrorService from '../../../services/ErrorService';
import { updateFilterAndDefinitionExpression, updateFilterExpression } from '../../../utils/ExpressionHelper';
import { getBaseFilterString } from '../../../utils/QueryHelper';
import InFormAlert from '../../InFormAlert';
import "../defaults/CustomFiltersContainer.css";
import { buildFilter } from '../utils/sqlUtils';
import CustomFiltersDialog from './CustomFiltersDialog';
import ShareFilterDialog from './ShareFilterDialog';

const useStyles = makeStyles(() => ({
  filterCollapser: {
    display:'flex', 
    flex: '0 0 56px', 
    width: '100%', 
    alignItems: 'center', 
    textAlign: 'center',
    boxSizing: 'border-box', 
    cursor: 'pointer', 
    transition: 'color 275ms cubic-bezier(0.4, 0.0, 0.2, 1)', 
    backgroundColor: ColorConstants.WhiteAlien,
    color: ColorConstants.BlueAlien, 
    justifyContent:'center', 
    '&:hover': {
      backgroundColor: ColorConstants.BlueAlien,
      color: ColorConstants.WhiteAlien
    }
  },
  filterItemSelection: {
    display:'flex', 
    justifyContent:'flex-start', 
    padding: '10px 4px 10px 10px',
    backgroundColor: ColorConstants.WhiteAlien, 
    color: ColorConstants.BlueAlien, 
    width: '100%',
    flexDirection: 'row',
    borderBottom: '1px solid', 
    borderBottomColor: ColorConstants.BlueAlien,
    '&:hover': {
      backgroundColor: 'transparent',
    }
  },
  filterCollapserButton: {
    display:'flex', 
    flex: '0 0 56px', 
    width: '100%', 
    alignItems: 'center', 
    textAlign: 'center',
    boxSizing: 'border-box', 
    cursor: 'pointer', 
    transition: 'color 275ms cubic-bezier(0.4, 0.0, 0.2, 1)', 
    backgroundColor: ColorConstants.WhiteAlien,
    color: ColorConstants.BlueAlien, 
    justifyContent:'center', 
    borderBottom: '1px solid', 
    borderBottomColor: ColorConstants.BlueAlien,
    '&:hover': {
      backgroundColor: 'transparent',
    }
  },
}));

const theme = createTheme({
  overrides: {
    MuiListItemButton: {
      root: {
        margin: 0,
        padding: 10,
        '&:hover $filterCollapser': {
          color: ColorConstants.BlueAlien,
        },
      },
    },
  },
});

export default function CustomFiltersList({userInfo}) {
  const dispatch = useDispatch();
  const [showingCustomFilterDialog, setShowingCustomFilterDialog] = useState(false);
  const [filterToRemove, setFilterToRemove] = useState(null);
  const [showRemoveFilterWarning, setShowRemoveFilterWarning] = useState(false);
  const [removeFilterWarningRemoveAll, setRemoveFilterWarningRemoveAll] = useState(false);
  const [filterForEdit, setFilterForEdit] = useState(null);
  const [customSortedFilterListItems, setCustomSortedFilterListItems] = useState([]);
  const [filterTitleLocal, setFilterTitleLocal] = useState("");
  const classes = useStyles();
  const config = useSelector((state) => state.configuration.config);
  const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
  const nifcToken = useSelector((state) => state.configuration.nifcToken);
  const [showShareFilterDialog, setShowShareFilterDialog] = useState(false);

  const { treatmentCount, activityCount, actualTreatmentCount, actualActivityCount, 
    treatmentCountQueryError, activityCountQueryError, actualTreatmentCountQueryError, actualActivityCountQueryError, isTreatmentCountQuerying, isActivityCountQuerying, 
    isActualTreatmentCountQuerying, isActualActivityCountQuerying } = useIFPRSGlobalContext();
  
  useEffect(() => {
     const activeFilters = customSortedFilterListItems.filter(cf => cf[FieldNames.customFilter.isActive] === FieldNames.yesNo.yes);
     dispatch(setCustomFilterCount(activeFilters.length));
     }, [customSortedFilterListItems]);

  useEffect(() => {
    if(userInfo && config){
      CustomFilterService.getCustomFiltersByUser(userInfo[FieldNames.userFields.userID]).then(resp => {
        if(resp && resp.length > 0){
          let allItems = [];
          resp.forEach(item => {
            if(item && item.attributes){
              let parsedSets = null;
              try{
                parsedSets = JSON.parse(item.attributes[FieldNames.customFilter.sets]);
              } catch(error){
                console.log(error);
              }

              let newItem = {
                [FieldNames.customFilter.mustMatchAll]: item.attributes[FieldNames.customFilter.mustMatchAll] === FieldNames.yesNo.no ? false : true,
                [FieldNames.customFilter.expression]: item.attributes[FieldNames.customFilter.expression],
                [FieldNames.customFilter.sets]: parsedSets,
                [FieldNames.customFilter.title]: item.attributes[FieldNames.customFilter.title] ? item.attributes[FieldNames.customFilter.title]?.replaceAll('&lt;', '<')?.replaceAll('&gt;', '>') : '',
                [FieldNames.customFilter.isActive]: item.attributes[FieldNames.customFilter.isActive],
                [FieldNames.customFilter.userID]: item.attributes[FieldNames.customFilter.userID],
                [FieldNames.customFilter.userCustomFiltersID]: item.attributes[FieldNames.customFilter.userCustomFiltersID],
                [FieldNames.customFilter.objectID]: item.attributes[FieldNames.customFilter.objectID],
                [FieldNames.customFilter.dataset]: item.attributes[FieldNames.customFilter.dataset]
              }
              allItems.push(newItem);
            }
          });
          //Sort mutates the array so no need to assign it to another variable
          allItems.sort((a, b) => (a[FieldNames.customFilter.title] > b[FieldNames.customFilter.title]) ? 1 : -1);
          setCustomSortedFilterListItems(allItems);

          let activeCustomFilter = allItems.filter(cf => cf[FieldNames.customFilter.isActive] === FieldNames.yesNo.yes);
          if(activeCustomFilter && activeCustomFilter.length > 0)
          {
            let item = activeCustomFilter[0];
            let temp = JSON.parse(JSON.stringify(item));
            let activeDefinitionExpression = buildFilter(temp, false, userInfo);
            dispatch(setActiveFilter(temp, activeDefinitionExpression));
            dispatch(setFilterExpression(activeDefinitionExpression));
          }
        } else {
          setCustomSortedFilterListItems([]);
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
    
  }, [userInfo])

  const launchCustomFilters = (event) =>
  {
    event.preventDefault();
    setFilterTitleLocal("");
    setShowingCustomFilterDialog(true);
  };

  const customFiltersClose = () =>
  {
    setShowingCustomFilterDialog(false);
    setFilterForEdit(null);
  }

  const filterHeader = () => 
  {
    const trtCountVal = (treatmentCountQueryError) ? "-" : treatmentCount;
    const actCountVal = (activityCountQueryError) ? "-" : activityCount;
    const treatmentIndicator = (treatmentCount === 1) ? "Estimated Treatment" : "Estimated Treatments";
    const treatmentLabel = trtCountVal + " " + treatmentIndicator;
    const activityIndicator = (activityCount === 1) ? "Estimated Activity" : "Estimated Activities";
    const activityLabel = actCountVal + " " + activityIndicator;
    const label = treatmentLabel + ", " + activityLabel;
    return ((isTreatmentCountQuerying || isActivityCountQuerying || isActualTreatmentCountQuerying || isActualActivityCountQuerying)) ? (<CircularProgress size={10} />) : label;
  }

  const filterHeaderActuals = () => 
  {
    const trtCountVal = (actualTreatmentCountQueryError) ? "-" : actualTreatmentCount;
    const actCountVal = (actualActivityCountQueryError) ? "-" : actualActivityCount;
    const actualsTreatmentIndicator = (actualTreatmentCount === 1) ? "Actual Treatment" : "Actual Treatments";
    const actualsTreatmentLabel = trtCountVal + " " + actualsTreatmentIndicator;
    const actualsActivityIndicator = (actualActivityCount === 1) ? "Actual Activity" : "Actual Activities";
    const actualsActivityLabel = actCountVal + " " + actualsActivityIndicator;
    const label = actualsTreatmentLabel + ", " + actualsActivityLabel;
    return label;
  }


  const editFilter = (item) =>
  {
    setShowingCustomFilterDialog(true);
    //filterForEdit is how we know this is an update, if null then we add a new filter
    setFilterForEdit(item);
    setFilterTitleLocal(item[FieldNames.customFilter.title]);
  }

  const duplicateFilter = (item) =>
  {
    let clonedItem = JSON.parse(JSON.stringify(item));
    clonedItem[FieldNames.customFilter.filterGuid] = uuidv4();
    clonedItem[FieldNames.customFilter.userCustomFiltersID] = uuidv4();
    clonedItem[FieldNames.customFilter.title] = "Copy of " + clonedItem[FieldNames.customFilter.title];
    clonedItem[FieldNames.customFilter.objectID] = null;

    let newItem = {attributes: {}};
    newItem.attributes[FieldNames.customFilter.userID] = clonedItem[FieldNames.customFilter.userID];
    newItem.attributes[FieldNames.customFilter.mustMatchAll] = clonedItem[FieldNames.customFilter.mustMatchAll];
    newItem.attributes[FieldNames.customFilter.expression] = clonedItem[FieldNames.customFilter.expression];
    newItem.attributes[FieldNames.customFilter.sets] = JSON.stringify(clonedItem[FieldNames.customFilter.sets]); 
    newItem.attributes[FieldNames.customFilter.title] = clonedItem[FieldNames.customFilter.title];
    newItem.attributes[FieldNames.customFilter.isActive] = FieldNames.yesNo.yes;
    newItem.attributes[FieldNames.customFilter.userCustomFiltersID] = clonedItem[FieldNames.customFilter.filterGuid];
    newItem.attributes[FieldNames.customFilter.usersJoinId] = userInfo[FieldNames.userFields.id];
    
    CustomFilterService.addCustomFilter(newItem).then(response => {
      if(response && response.addResults && response.addResults.length > 0 && response.addResults[0].success){
        clonedItem[FieldNames.customFilter.objectID] = response.addResults[0].objectId;
        //Setting is important for the popup
        setFilterTitleLocal(clonedItem[FieldNames.customFilter.title]);
        setFilterForEdit(clonedItem);
        let allFilters = JSON.parse(JSON.stringify(customSortedFilterListItems));
        //Add the new filter to the list
        allFilters.push(clonedItem);
        //Reorder list
        let sortedAllItems = allFilters.sort((a, b) => (a[FieldNames.customFilter.title] > b[FieldNames.customFilter.title]) ? 1 : -1);
        //Update the list
        setCustomSortedFilterListItems(sortedAllItems);
        //Put this last so that all information is there when the popup loads
        setShowingCustomFilterDialog(true);
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
    });
  }

  const filterListItemClicked = (item) =>
  {
    let tempItems = [];
    let tempArray = [];
    customSortedFilterListItems.forEach(eaFilter => {
      if(eaFilter[FieldNames.customFilter.userCustomFiltersID] === item[FieldNames.customFilter.userCustomFiltersID]){
        eaFilter[FieldNames.customFilter.isActive] = FieldNames.yesNo.yes;
      } else {
        eaFilter[FieldNames.customFilter.isActive] = FieldNames.yesNo.no;
      }
      let newItem = {attributes: {}};
      newItem.attributes[FieldNames.customFilter.objectID] = eaFilter[FieldNames.customFilter.objectID];
      newItem.attributes[FieldNames.customFilter.userID] = eaFilter[FieldNames.customFilter.userID];
      newItem.attributes[FieldNames.customFilter.isActive] = eaFilter[FieldNames.customFilter.isActive];
      newItem.attributes[FieldNames.customFilter.userCustomFiltersID] = eaFilter[FieldNames.customFilter.userCustomFiltersID]; 
      tempArray.push(newItem);
      tempItems.push(eaFilter);
    });
    CustomFilterService.updateCustomFilter(tempArray).then(response => {
      if(response && response.updateResults && response.updateResults.length > 0 && response.updateResults[0].success){
        //Sort mutates the array so no need to assign it to another variable
        tempItems.sort((a, b) => (a[FieldNames.customFilter.title] > b[FieldNames.customFilter.title]) ? 1 : -1);
        setCustomSortedFilterListItems(tempItems);
        
        let temp = JSON.parse(JSON.stringify(item));
        let activeDefinitionExpression = buildFilter(temp, false, userInfo);
        dispatch(setActiveFilter(temp, activeDefinitionExpression));
        const finalStr = updateFilterExpression(userInfo, activeDefinitionExpression);
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
    });
  }

  const handleClose = () => {
    setShowRemoveFilterWarning(false);
    if(removeFilterWarningRemoveAll)
    {
      removeAllCustomFilters();
    }
    else
    {
      removeFilter(filterToRemove);
    }
  }

  const handleCancel = () => {
    setShowRemoveFilterWarning(false);
  }

  const removeFilterPrompt = (item) => {
    if(item !== null)
    {
      setFilterToRemove(item);
    }
    setRemoveFilterWarningRemoveAll(false);
    setShowRemoveFilterWarning(true);
  }

  const removeAllFilterPrompt = () => {
    setRemoveFilterWarningRemoveAll(true);
    setShowRemoveFilterWarning(true);
  }

  const shareFilter = (item) => {
    setShowShareFilterDialog(true);
    setFilterForEdit(item)
  }

  const shareFilterClose = () => {
    setShowShareFilterDialog(false);
  }

  const removeFilter = (item) => {
    if(item === null)
    {
      return;
    }
    const currentObjectID = item[FieldNames.customFilter.objectID];
    const isActiveFilter = item[FieldNames.customFilter.isActive] === FieldNames.yesNo.yes;
    CustomFilterService.deleteCustomFilter(currentObjectID).then(response => {
      if(response && response.success){
        let temp = JSON.parse(JSON.stringify(customSortedFilterListItems));
        let remainingFilters = temp.filter(x => x[FieldNames.customFilter.objectID] !== currentObjectID);
        let sortedAllItems = remainingFilters.sort((a, b) => (a[FieldNames.customFilter.title] > b[FieldNames.customFilter.title]) ? 1 : -1);
        setCustomSortedFilterListItems(sortedAllItems);
        const baseString = getBaseFilterString();
        if(isActiveFilter){
          let newItem = {
            expression: baseString
          }
          let tempItem = JSON.parse(JSON.stringify(newItem));
          dispatch(setActiveFilter(tempItem));
          const finalStr = updateFilterAndDefinitionExpression(userInfo);
        }
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
    });
  }

  const removeAllCustomFilters = () => {
    CustomFilterService.deleteAllCustomFiltersByUser(userInfo[FieldNames.userFields.userID]).then(response => {
      if(response && response.success){
        setCustomSortedFilterListItems([]);
        const baseString = getBaseFilterString();
        let newItem = {
          expression: baseString
        }
        let tempItem = JSON.parse(JSON.stringify(newItem));
        dispatch(setActiveFilter(tempItem));
        const finalStr = updateFilterAndDefinitionExpression(userInfo);
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
    });
  }

  const clearCustomFiltersHandler = () => {
    
    //Get all Custom filters that are set as active
    const activeCustomFilters = customSortedFilterListItems?.filter(x => x[FieldNames.customFilter.isActive] === FieldNames.yesNo.yes)?.map(x => x[FieldNames.customFilter.objectID])
    CustomFilterService.clearAllActiveCustomFilterByUser(userInfo[FieldNames.userFields.userID], activeCustomFilters).then(resp => {
      const baseString = getBaseFilterString();
      const modifiedCustomFilters = [];
      let updateSuccess = true;
      const savedResults = resp?.updatedResults?.filter(x => !x?.success);
      if(savedResults?.length > 0){
        updateSuccess = false;
      }
      if(updateSuccess){
        customSortedFilterListItems.forEach(eaFilter =>
            modifiedCustomFilters.push({...eaFilter, [FieldNames.customFilter.isActive]: FieldNames.yesNo.no})
        );
        setCustomSortedFilterListItems(modifiedCustomFilters)
        dispatch(setActiveFilter(baseString));
        dispatch(setDefinitionExpression(baseString))
        dispatch(setFilterExpression(baseString))
      }
    })
  }

    return (
      <div className="custom-filter-details">
        <div style={{ top: 64 }}>
          <InFormAlert
            openFlag={showRemoveFilterWarning}
            closeMethod={handleCancel}
            buttonMethod={handleClose}
            titleText={"Custom Filter Deletion"}
            contentText={"Are you sure you would like to delete these filter(s)?"}
          />
        </div>
        <ThemeProvider theme={theme}>
          <div className="custom-filterList-panel">
          <List 
              sx={{ height: '100%', overflowY: 'hidden', paddingTop: 0, paddingBottom: 0 }}
              component="nav" className="custom-filter-list"
              aria-labelledby="nested-list" 
            >
              <ListItemButton onClick={clearCustomFiltersHandler} className={classes.filterCollapserButton} >
                <ListItemText primary="Reset Filters" />
              </ListItemButton>
              <ListItem className="custom-filter-header" style={{display:'flex', justifyContent:'center', margin:'0px', zIndex: '5', color: ColorConstants.BlueAlien, backgroundColor: ColorConstants.LightGrayAlien,
                fontWeight: 300, fontStyle: 'italic', textAlign: 'center', boxSizing: 'border-box', borderBottom: '1px solid', borderBottomColor: ColorConstants.BlueAlien, height: 'auto', minHeight: '55px'}}>
                {(isTreatmentCountQuerying || isActivityCountQuerying || isActualTreatmentCountQuerying || isActualActivityCountQuerying) ? 
                  (<CircularProgress size={10} />) : (<ListItemText style={{ wordBreak: 'break-word'}} primary={filterHeader()} 
                  secondary={
                    <React.Fragment>
                      <Typography
                        sx={{ display: 'inline' }}
                        component="span"
                        variant="body2"
                        color={ColorConstants.BlueAlien}
                        fontStyle={'italic'}
                        fontWeight={300}
                        fontSize={16}
                      >
                        {(isTreatmentCountQuerying || isActivityCountQuerying || isActualTreatmentCountQuerying || isActualActivityCountQuerying) ? '' : filterHeaderActuals()}
                      </Typography>
                    </React.Fragment>
                  } 
                />)}
              </ListItem>
              <ListItem>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={launchCustomFilters}>Add Custom Filter</Button>
              </ListItem>
              {customSortedFilterListItems.map((filter, i) => (
                <ListItem key={i} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                  <Button
                    color="primary"
                    style={{ wordBreak: 'break-word', textAlign: 'left', justifyContent: 'left', textWrap: 'wrap', width: '75%'}}
                    variant={(filter[FieldNames.customFilter.isActive] === FieldNames.yesNo.yes) ? "contained" : "text"}
                    onClick={() => { filterListItemClicked(filter) } }>{filter && filter[FieldNames.customFilter.title] ? filter[FieldNames.customFilter.title] : ""}</Button>
                  <span style={{ width: '25%'}}>
                    <SendIcon
                      className="filter-item-header-clear"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        shareFilter(filter);
                      }}
                      style={{height: '25px', float: "right", paddingTop:"8px"}} 
                    />
                    <CallMadeIcon
                      className="filter-item-header-clear"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        duplicateFilter(filter)
                      }}
                      style={{height: '25px', float: "right", paddingTop:"8px"}} 
                    />
                    <EditIcon
                      color="primary"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editFilter(filter)
                      }}
                      style={{height: '25px', float: "right", paddingTop:"8px"} } 
                    />
                    <DeleteIcon
                      className="filter-item-header-clear"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeFilterPrompt(filter);
                      }}
                      style={{height: '25px', float: "right", paddingTop:"8px"}} 
                    />
                  </span>
                </ListItem>
              ))}
              <ListItemButton onClick={removeAllFilterPrompt} className={classes.filterCollapser} >
                <ListItemText primary="Delete All Custom Filters" />
              </ListItemButton>
            </List>
            {showingCustomFilterDialog && 
              <CustomFiltersDialog
                open={showingCustomFilterDialog}
                onClose={customFiltersClose}
                filterForEdit={filterForEdit}
                customSortedFilterListItems={customSortedFilterListItems}
                setCustomSortedFilterListItems={setCustomSortedFilterListItems}
                userInfo={userInfo}
                filterTitleLocal={filterTitleLocal}
                setFilterTitleLocal={setFilterTitleLocal}
              />
            }
            {showShareFilterDialog &&
              <ShareFilterDialog open={showShareFilterDialog}
                onClose={shareFilterClose}
                filterForShare={filterForEdit}
              />
            }
          </div>
        </ThemeProvider>
      </div>
    )
}